import React, { useState, useEffect, useContext } from 'react';
import { NavBarNew } from '../../componentes/navBarNew';
import { DefaultContext } from '../../context/context_default';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";

const BannerAdmin = () => {
	const { globalFirestoreData } = useContext(DefaultContext);
	const [banners, setBanners] = useState([]);
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = useState({
		titulo: '',
		url: '',
		ordem: 0,
		imageUrl: ''
	});
	const [editingId, setEditingId] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const [isUploading, setIsUploading] = useState(false);

	useEffect(() => {
		fetchBanners();
	}, []);

	const fetchBanners = async () => {
		try {
			const bannersRef = collection(db, "banners");
			const q = query(bannersRef, orderBy("ordem", "asc"));
			const querySnapshot = await getDocs(q);

			const bannersData = [];
			querySnapshot.forEach((doc) => {
				bannersData.push({ id: doc.id, ...doc.data() });
			});

			setBanners(bannersData);
			setLoading(false);
		} catch (error) {
			console.error("Erro ao buscar banners:", error);
			setLoading(false);
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: name === 'ordem' ? parseInt(value) || 0 : value
		});
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedFile(file);
			const fileReader = new FileReader();
			fileReader.onload = () => {
				setPreviewUrl(fileReader.result);
			};
			fileReader.readAsDataURL(file);
		}
	};

	const uploadImage = async (file) => {
		if (!file) return null;

		setIsUploading(true);
		const storageRef = ref(storage, `banners/${Date.now()}_${file.name}`);
		const uploadTask = uploadBytesResumable(storageRef, file);

		return new Promise((resolve, reject) => {
			uploadTask.on(
				'state_changed',
				(snapshot) => {
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setUploadProgress(progress);
				},
				(error) => {
					console.error("Erro no upload:", error);
					setIsUploading(false);
					reject(error);
				},
				async () => {
					const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
					setIsUploading(false);
					setUploadProgress(0);
					resolve(downloadURL);
				}
			);
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			let imageUrl = formData.imageUrl;

			if (selectedFile) {
				imageUrl = await uploadImage(selectedFile);
			}

			if (!imageUrl) {
				alert("É necessário selecionar uma imagem!");
				return;
			}

			const bannerData = {
				titulo: formData.titulo,
				url: formData.url,
				ordem: formData.ordem,
				imageUrl: imageUrl,
				timestamp: serverTimestamp()
			};

			if (editingId) {
				// Atualizar banner existente
				await updateDoc(doc(db, "banners", editingId), bannerData);
				alert("Banner atualizado com sucesso!");
			} else {
				// Adicionar novo banner
				await addDoc(collection(db, "banners"), bannerData);
				alert("Banner adicionado com sucesso!");
			}

			// Resetar formulário e recarregar banners
			resetForm();
			fetchBanners();
		} catch (error) {
			console.error("Erro ao salvar banner:", error);
			alert("Erro ao salvar banner. Tente novamente.");
		}
	};

	const handleEdit = (banner) => {
		setFormData({
			titulo: banner.titulo || '',
			url: banner.url || '',
			ordem: banner.ordem || 0,
			imageUrl: banner.imageUrl || ''
		});
		setEditingId(banner.id);
		setPreviewUrl(banner.imageUrl);
		setSelectedFile(null);
	};

	const handleDelete = async (bannerId, imageUrl) => {
		if (!window.confirm("Tem certeza que deseja excluir este banner?")) return;

		try {
			// Excluir documento do Firestore
			await deleteDoc(doc(db, "banners", bannerId));

			// Tentar excluir a imagem do Storage
			if (imageUrl) {
				try {
					const imageRef = ref(storage, imageUrl);
					await deleteObject(imageRef);
				} catch (storageError) {
					console.error("Erro ao excluir imagem do storage:", storageError);
					// Continua mesmo se falhar a exclusão da imagem
				}
			}

			alert("Banner excluído com sucesso!");
			fetchBanners();
		} catch (error) {
			console.error("Erro ao excluir banner:", error);
			alert("Erro ao excluir banner. Tente novamente.");
		}
	};

	const resetForm = () => {
		setFormData({
			titulo: '',
			url: '',
			ordem: 0,
			imageUrl: ''
		});
		setEditingId(null);
		setSelectedFile(null);
		setPreviewUrl('');
	};

	// Verificar se o usuário é admin
	if (globalFirestoreData.role !== "admin") {
		return (
			<div className="min-h-screen bg-gray-100">
				{/* <NavBarNew /> */}
				<div className="container mx-auto px-4 py-8">
					<div className="bg-white p-6 rounded-lg shadow-md">
						<h1 className="text-2xl font-bold text-red-600 mb-4">Acesso Negado</h1>
						<p>Você não tem permissão para acessar esta página.</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-100">
			{/* <NavBarNew /> */}
			<div className="container mx-auto px-4 py-8">
				<h1 className="text-3xl font-bold mb-6">Gerenciador de Banners</h1>

				{/* Formulário */}
				<div className="bg-white p-6 rounded-lg shadow-md mb-8">
					<h2 className="text-xl font-semibold mb-4">{editingId ? 'Editar Banner' : 'Adicionar Novo Banner'}</h2>
					<form onSubmit={handleSubmit}>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<div className="mb-4">
									<label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="titulo">
										Título
									</label>
									<input
										type="text"
										id="titulo"
										name="titulo"
										value={formData.titulo}
										onChange={handleInputChange}
										className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
									/>
								</div>
								<div className="mb-4">
									<label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="url">
										URL (link ao clicar)
									</label>
									<input
										type="text"
										id="url"
										name="url"
										value={formData.url}
										onChange={handleInputChange}
										className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
									/>
								</div>
								<div className="mb-4">
									<label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ordem">
										Ordem
									</label>
									<input
										type="number"
										id="ordem"
										name="ordem"
										value={formData.ordem}
										onChange={handleInputChange}
										className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
									/>
								</div>
							</div>
							<div>
								<div className="mb-4">
									<label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="imagem">
										Imagem do Banner
									</label>
									<input
										type="file"
										id="imagem"
										accept="image/*"
										onChange={handleFileChange}
										className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
									/>
								</div>
								{isUploading && (
									<div className="mb-4">
										<div className="w-full bg-gray-200 rounded-full h-2.5">
											<div
												className="bg-blue-600 h-2.5 rounded-full"
												style={{ width: `${uploadProgress}%` }}
											></div>
										</div>
										<p className="text-sm text-gray-600 mt-1">{Math.round(uploadProgress)}% Completo</p>
									</div>
								)}
								{previewUrl && (
									<div className="mb-4">
										<p className="block text-gray-700 text-sm font-bold mb-2">Prévia:</p>
										<img
											src={previewUrl}
											alt="Preview"
											className="max-w-full h-auto max-h-40 border rounded"
										/>
									</div>
								)}
							</div>
						</div>
						<div className="flex items-center justify-between mt-4">
							<div>
								<button
									type="submit"
									disabled={isUploading}
									className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
								>
									{editingId ? 'Atualizar' : 'Adicionar'}
								</button>
								{editingId && (
									<button
										type="button"
										onClick={resetForm}
										className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
									>
										Cancelar
									</button>
								)}
							</div>
						</div>
					</form>
				</div>

				{/* Lista de Banners */}
				<div className="bg-white p-6 rounded-lg shadow-md">
					<h2 className="text-xl font-semibold mb-4">Banners Cadastrados</h2>
					{loading ? (
						<div className="flex justify-center">
							<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
						</div>
					) : banners.length > 0 ? (
						<div className="overflow-x-auto">
							<table className="min-w-full bg-white">
								<thead>
									<tr>
										<th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagem</th>
										<th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
										<th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
										<th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordem</th>
										<th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
									</tr>
								</thead>
								<tbody>
									{banners.map((banner) => (
										<tr key={banner.id}>
											<td className="py-2 px-4 border-b border-gray-200">
												<img
													src={banner.imageUrl}
													alt={banner.titulo || "Banner"}
													className="h-16 w-auto object-cover"
												/>
											</td>
											<td className="py-2 px-4 border-b border-gray-200">{banner.titulo || "Sem título"}</td>
											<td className="py-2 px-4 border-b border-gray-200">
												{banner.url ? (
													<a href={banner.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
														{banner.url.length > 30 ? banner.url.substring(0, 30) + '...' : banner.url}
													</a>
												) : (
													"Sem URL"
												)}
											</td>
											<td className="py-2 px-4 border-b border-gray-200">{banner.ordem}</td>
											<td className="py-2 px-4 border-b border-gray-200">
												<button
													onClick={() => handleEdit(banner)}
													className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-2 rounded text-xs mr-2"
												>
													Editar
												</button>
												<button
													onClick={() => handleDelete(banner.id, banner.imageUrl)}
													className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs"
												>
													Excluir
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<p className="text-gray-500">Nenhum banner cadastrado.</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default BannerAdmin;